"use client";

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Trophy, 
  Star, 
  Search, 
  Shield, 
  Bug, 
  AlertTriangle, 
  ChevronDown, 
  Filter, 
  Zap, 
  Gift, 
  CheckCircle, 
  Award, 
  Medal,
  Sparkles
} from 'lucide-react';
import Link from 'next/link';

import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectItem, SelectTrigger, SelectValue, SelectContent } from '@/components/ui/select';

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: "spring" as const, stiffness: 70 }
  }
};

const shimmer = {
  hidden: { opacity: 0.3 },
  visible: { 
    opacity: 1,
    transition: { 
      repeat: Infinity, 
      repeatType: "reverse", 
      duration: 1.5
    }
  }
} as const;

type Researcher = {
  id: number;
  name: string;
  username: string;
  profileUrl?: string;
  avatar: string;
  achievements: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  badges: string[];
  specialties: string[];
  totalRewards: string;
  joinedDate: string;
  rank: number;
  featured: boolean;
  country: string;
  contributions: {
    date: string;
    type: 'critical' | 'high' | 'medium' | 'low';
    description: string;
    reward?: string;
  }[];
};

export default function SecurityHallOfFameClient() {
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [timeframe, setTimeframe] = useState('all-time');
  const [researchers, setResearchers] = useState<Researcher[]>(mockResearchers);

  // `filteredResearchers` is derived from other state and props, so we compute it with `useMemo` instead of storing it in state.
  const filteredResearchers = React.useMemo(() => {
    // Apply filters and search
    let filtered = [...researchers];
    
    // Apply time filter
    if (timeframe === 'month') {
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      filtered = filtered.filter(r =>
        r.contributions.some(c => new Date(c.date) >= oneMonthAgo)
      );
    } else if (timeframe === 'year') {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      filtered = filtered.filter(r =>
        r.contributions.some(c => new Date(c.date) >= oneYearAgo)
      );
    }
    
    // Apply type filter
    if (filter !== 'all') {
      filtered = filtered.filter(r =>
        r.contributions.some(c => c.type === filter) ||
        r.specialties.includes(filter)
      );
    }
    
    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.name.toLowerCase().includes(query) ||
        r.username.toLowerCase().includes(query) ||
        r.specialties.some(s => s.toLowerCase().includes(query))
      );
    }
    
    // Sort by rank
    return filtered.sort((a, b) => a.rank - b.rank);
  }, [filter, searchQuery, timeframe, researchers]);

  const getContributionBadge = (type: string) => {
    switch (type) {
      case 'critical':
        return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">Critical</Badge>;
      case 'high':
        return <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200">High</Badge>;
      case 'medium':
        return <Badge variant="outline" className="bg-yellow-200 text-yellow-900 border-yellow-300">Medium</Badge>;
      case 'low':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">Low</Badge>;
      default:
        return null;
    }
  };
  
  const getMedalIcon = (rank: number) => {
    if (rank === 1) return <Medal className="h-6 w-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="h-6 w-6 text-gray-400" />;
    if (rank === 3) return <Medal className="h-6 w-6 text-yellow-600" />;
    return null;
  };

  return (
    <div className="min-h-screen">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative py-20 overflow-hidden"
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 bg-linear-to-br from-blue-700 via-blue-600 to-blue-800 overflow-hidden">
          <motion.div 
            className="absolute top-0 left-0 right-0 h-1/2 w-full"
            style={{
              background: 'radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.15) 0%, transparent 60%)'
            }}
            animate={{ 
              y: [0, 10, 0], 
              opacity: [0.7, 0.9, 0.7] 
            }}
            transition={{ 
              duration: 8, 
              repeat: Infinity,
              ease: "easeInOut" 
            }}
          />
          
          {/* Floating particles */}
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={`particle-${i}`}
              className="absolute rounded-full bg-white/10"
              style={{
                width: Math.random() * 10 + 4,
                height: Math.random() * 10 + 4,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -50],
                opacity: [0, 0.7, 0],
                scale: [0, 1, 0.5]
              }}
              transition={{
                duration: Math.random() * 5 + 7,
                repeat: Infinity,
                delay: Math.random() * 10,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                type: "spring", 
                stiffness: 100, 
                damping: 15
              }}
              className="mx-auto mb-8 inline-flex"
            >
              <motion.div 
                whileHover={{ scale: 1.05, rotate: 5 }}
                className="rounded-full bg-white/15 p-5 backdrop-blur-sm shadow-lg border border-white/20 hover:bg-white/20"
              >
                <Trophy className="h-14 w-14 text-yellow-200" />
                <motion.div 
                  variants={shimmer}
                  initial="hidden"
                  animate="visible"
                  className="absolute -inset-1 rounded-full bg-white/20 blur-md -z-10"
                />
              </motion.div>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-4xl md:text-6xl font-bold text-white mb-6"
            >
              <span className="relative">
                Security
                <motion.span
                  className="absolute -top-6 -right-8"
                  animate={{ rotate: [0, 10, 0], scale: [1, 1.1, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <Sparkles className="h-8 w-8 text-yellow-200" />
                </motion.span>
              </span>{" "}
              <span className="bg-clip-text text-transparent bg-linear-to-r from-yellow-200 via-yellow-100 to-yellow-300">
                Hall of Fame
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed"
            >
              Celebrating the brilliant security researchers who help protect RentWise 
              and our users through responsible disclosure.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link href="/bug-bounty">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20 backdrop-blur-sm px-6 py-6 h-auto font-medium">
                    <motion.span
                      animate={{ 
                        x: [0, 5, 0],
                      }}
                      transition={{ 
                        duration: 1.5, 
                        repeat: Infinity,
                        repeatType: "reverse",
                        ease: "easeInOut",
                        delay: 1
                      }}
                      className="mr-2"
                    >
                      <Bug className="h-5 w-5" />
                    </motion.span>
                    Join Bug Bounty Program
                  </Button>
                </motion.div>
              </Link>
              <Link href="/vulnerability">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button variant="secondary" className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 border-yellow-300 px-6 py-6 h-auto font-semibold shadow-lg">
                    <Shield className="h-5 w-5 mr-2" />
                    Report Vulnerability
                  </Button>
                </motion.div>
              </Link>
            </motion.div>
          </div>
        </div>
        
        {/* Animated wave divider */}
        <div className="absolute bottom-0 left-0 right-0 h-16 overflow-hidden">
          <svg
            preserveAspectRatio="none"
            className="h-full w-full"
            viewBox="0 0 1200 120"
            xmlns="http://www.w3.org/2000/svg"
            style={{ fill: '#ffffff' }}
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              opacity=".25"
            />
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              opacity=".5"
            />
            <path
              d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
            />
          </svg>
        </div>
      </motion.div>
      
      <div className="container mx-auto px-4 py-12">
        <Tabs defaultValue="leaderboard" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <TabsList className="bg-white/80 backdrop-blur-sm rounded-xl p-1 shadow-md">
              <TabsTrigger value="leaderboard" className="text-sm px-5 py-2.5 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white font-medium transition-all">
                <Trophy className="h-4 w-4 mr-2" /> Leaderboard
              </TabsTrigger>
              <TabsTrigger value="contributions" className="text-sm px-5 py-2.5 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white font-medium transition-all">
                <Bug className="h-4 w-4 mr-2" /> Recent Contributions
              </TabsTrigger>
              <TabsTrigger value="about" className="text-sm px-5 py-2.5 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white font-medium transition-all">
                <Shield className="h-4 w-4 mr-2" /> About
              </TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input 
                  placeholder="Search researchers..." 
                  className="pl-10 border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger className="w-[130px] border-gray-200 shadow-sm">
                  <SelectValue placeholder="Timeframe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-time">All Time</SelectItem>
                  <SelectItem value="year">Past Year</SelectItem>
                  <SelectItem value="month">Past Month</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[130px] border-gray-200 shadow-sm">
                  <SelectValue placeholder="Filter by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="web">Web Security</SelectItem>
                  <SelectItem value="api">API Security</SelectItem>
                  <SelectItem value="mobile">Mobile Security</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <TabsContent value="leaderboard" className="mt-0">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {/* Featured researchers - Top 3 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                {filteredResearchers
                  .filter(r => r.rank <= 3)
                  .sort((a, b) => a.rank - b.rank)
                  .map((researcher) => (
                    <motion.div 
                      key={researcher.id}
                      variants={fadeInUp}
                      className={`col-span-1 ${
                        researcher.rank === 1 ? "md:order-2" : 
                        researcher.rank === 2 ? "md:order-1" : 
                        "md:order-3"
                      }`}
                      whileHover={{ 
                        y: -10,
                        transition: { type: "spring", stiffness: 300, damping: 10 }
                      }}
                    >
                      <Card className={`overflow-hidden relative h-full border-0 shadow-lg ${
                        researcher.rank === 1 ? "bg-linear-to-b from-yellow-100 to-white" : 
                        researcher.rank === 2 ? "bg-linear-to-b from-gray-50 to-white" : 
                        "bg-linear-to-b from-yellow-200/10 to-white"
                      }`}>
                        {researcher.rank === 1 && (
                          <motion.div 
                            className="absolute top-0 left-0 right-0 h-1.5 bg-linear-to-r from-yellow-300 via-yellow-200 to-yellow-300" 
                            animate={{
                              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                            }}
                            transition={{
                              duration: 10,
                              repeat: Infinity,
                              ease: "linear"
                            }}
                            style={{
                              backgroundSize: '200% 200%'
                            }}
                          />
                        )}
                        {researcher.rank === 2 && (
                          <div className="absolute top-0 left-0 right-0 h-1.5 bg-linear-to-r from-gray-300 to-gray-400" />
                        )}
                        {researcher.rank === 3 && (
                          <div className="absolute top-0 left-0 right-0 h-1.5 bg-linear-to-r from-yellow-500 to-yellow-600" />
                        )}
                        
                        <div className={`p-8 text-center relative`}>
                          {/* Subtle background decoration */}
                          {researcher.rank === 1 && (
                            <motion.div 
                              className="absolute top-0 left-0 right-0 h-32 opacity-10"
                              style={{
                                background: 'radial-gradient(circle at center, rgba(251, 191, 36, 0.8) 0%, transparent 70%)'
                              }}
                              animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.1, 0.15, 0.1]
                              }}
                              transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                            />
                          )}
                          
                          <div className="relative inline-block mb-2">
                            <motion.div 
                              className={`relative rounded-full h-24 w-24 mb-3 mx-auto flex items-center justify-center text-2xl font-bold ${
                                researcher.rank === 1 ? "bg-linear-to-br from-yellow-300 to-yellow-400 text-gray-900" :
                                researcher.rank === 2 ? "bg-linear-to-br from-gray-300 to-gray-400 text-white" :
                                "bg-linear-to-br from-yellow-500 to-yellow-600 text-white"
                              }`}
                              whileHover={{ rotate: 5 }}
                              transition={{ type: "spring", stiffness: 200 }}
                            >
                              {researcher.avatar}
                              
                              {/* Animated glow effect for rank 1 */}
                              {researcher.rank === 1 && (
                                <motion.div 
                                  className="absolute -inset-1.5 rounded-full opacity-40 blur-sm -z-10"
                                  animate={{
                                    scale: [1, 1.2, 1],
                                    opacity: [0.3, 0.6, 0.3],
                                  }}
                                  transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                  }}
                                  style={{
                                    background: 'linear-gradient(45deg, #f59e0b, #fcd34d)'
                                  }}
                                />
                              )}
                            </motion.div>
                            
                            <motion.div 
                              className={`absolute -bottom-2 -right-2 rounded-full p-1.5 shadow-md ${
                                researcher.rank === 1 ? "bg-yellow-300" :
                                researcher.rank === 2 ? "bg-gray-400" :
                                "bg-yellow-500"
                              }`}
                              whileHover={{ 
                                scale: 1.2,
                                rotate: 10
                              }}
                              transition={{ type: "spring", stiffness: 200 }}
                            >
                              {getMedalIcon(researcher.rank)}
                            </motion.div>
                          </div>
                          
                          <h3 className="text-xl font-bold mb-1 text-gray-800">{researcher.name}</h3>
                          <p className="text-sm text-gray-500 mb-4">@{researcher.username}</p>
                          
                          <div className="flex flex-wrap justify-center gap-2 mb-5">
                            {researcher.badges.slice(0, 3).map((badge, idx) => (
                              <motion.div 
                                key={idx}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <Badge variant="secondary" className="bg-blue-50 text-blue-700 font-medium px-3 py-1 border border-blue-100">
                                  {badge}
                                </Badge>
                              </motion.div>
                            ))}
                          </div>
                          
                          <div className="grid grid-cols-4 gap-2 text-center mb-5">
                            <motion.div 
                              className="bg-red-50 p-2 rounded-lg shadow-sm border border-red-100"
                              whileHover={{ y: -3 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <div className="text-sm font-bold text-red-700">{researcher.achievements.critical}</div>
                              <div className="text-xs text-red-600">Critical</div>
                            </motion.div>
                            <motion.div 
                              className="bg-orange-50 p-2 rounded-lg shadow-sm border border-orange-100"
                              whileHover={{ y: -3 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <div className="text-sm font-bold text-orange-700">{researcher.achievements.high}</div>
                              <div className="text-xs text-orange-600">High</div>
                            </motion.div>
                            <motion.div 
                              className="bg-yellow-100 p-2 rounded-lg shadow-sm border border-yellow-200"
                              whileHover={{ y: -3 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <div className="text-sm font-bold text-yellow-800">{researcher.achievements.medium}</div>
                              <div className="text-xs text-yellow-700">Medium</div>
                            </motion.div>
                            <motion.div 
                              className="bg-blue-50 p-2 rounded-lg shadow-sm border border-blue-100"
                              whileHover={{ y: -3 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <div className="text-sm font-bold text-blue-700">{researcher.achievements.low}</div>
                              <div className="text-xs text-blue-600">Low</div>
                            </motion.div>
                          </div>
                          
                          <div className="text-sm">
                            <p className="font-medium flex items-center justify-center">
                              Total Rewards: 
                              <motion.span 
                                className="text-green-600 ml-1 flex items-center"
                                animate={{ scale: [1, 1.05, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                              >
                                <Gift className="h-3.5 w-3.5 mr-1 text-green-500" />
                                {researcher.totalRewards}
                              </motion.span>
                            </p>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                ))}
              </div>
            
              {/* Other researchers */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
                {filteredResearchers
                  .filter(r => r.rank > 3)
                  .map((researcher) => (
                    <motion.div 
                      key={researcher.id} 
                      variants={fadeInUp}
                      whileHover={{ 
                        y: -5,
                        transition: { type: "spring", stiffness: 200 }
                      }}
                    >
                      <Card className="overflow-hidden border-0 shadow hover:shadow-md transition-all duration-300 bg-white">
                        <CardContent className="p-5">
                          <div className="flex items-start gap-4">
                            <div className="relative">
                              <motion.div 
                                className="rounded-full h-14 w-14 bg-linear-to-br from-blue-100 to-blue-200 flex items-center justify-center text-lg font-medium text-blue-700 shadow-sm border border-blue-100/50"
                                whileHover={{ rotate: 5 }}
                                transition={{ type: "spring", stiffness: 200 }}
                              >
                                {researcher.avatar}
                              </motion.div>
                              <motion.div 
                                whileHover={{ scale: 1.2 }}
                                transition={{ type: "spring", stiffness: 300 }}
                                className="absolute -bottom-1 -right-1 bg-white rounded-full w-6 h-6 text-xs flex items-center justify-center border border-gray-200 font-bold text-gray-600 shadow-sm"
                              >
                                {researcher.rank}
                              </motion.div>
                            </div>
                            
                            <div className="flex-1">
                              <h3 className="font-bold text-gray-800">{researcher.name}</h3>
                              <p className="text-sm text-gray-500 mb-2">@{researcher.username}</p>
                              
                              <div className="flex gap-1 flex-wrap mb-3">
                                {researcher.badges.slice(0, 2).map((badge, idx) => (
                                  <motion.div
                                    key={idx}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700 border border-blue-100/60">
                                      {badge}
                                    </Badge>
                                  </motion.div>
                                ))}
                              </div>
                              
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="font-medium">Contributions:</span>
                                <div className="flex gap-1 ml-2">
                                  {researcher.achievements.critical > 0 && (
                                    <motion.span 
                                      className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded text-xs font-medium border border-red-200/60 shadow-sm"
                                      whileHover={{ 
                                        scale: 1.1,
                                        transition: { type: "spring", stiffness: 400 }
                                      }}
                                    >
                                      {researcher.achievements.critical}
                                    </motion.span>
                                  )}
                                  {researcher.achievements.high > 0 && (
                                    <motion.span 
                                      className="bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded text-xs font-medium border border-orange-200/60 shadow-sm"
                                      whileHover={{ 
                                        scale: 1.1,
                                        transition: { type: "spring", stiffness: 400 }
                                      }}
                                    >
                                      {researcher.achievements.high}
                                    </motion.span>
                                  )}
                                  {researcher.achievements.medium > 0 && (
                                    <motion.span 
                                      className="bg-yellow-200 text-yellow-900 px-1.5 py-0.5 rounded text-xs font-medium border border-yellow-300/60 shadow-sm"
                                      whileHover={{ 
                                        scale: 1.1,
                                        transition: { type: "spring", stiffness: 400 }
                                      }}
                                    >
                                      {researcher.achievements.medium}
                                    </motion.span>
                                  )}
                                  {researcher.achievements.low > 0 && (
                                    <motion.span 
                                      className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs font-medium border border-blue-200/60 shadow-sm"
                                      whileHover={{ 
                                        scale: 1.1,
                                        transition: { type: "spring", stiffness: 400 }
                                      }}
                                    >
                                      {researcher.achievements.low}
                                    </motion.span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                ))}
              </div>
              
              {filteredResearchers.length === 0 && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 100, damping: 15 }}
                  className="text-center py-16 bg-gray-50 rounded-xl border border-gray-100"
                >
                  <motion.div 
                    className="bg-gray-100 rounded-full p-5 inline-block mb-4 shadow-inner"
                    animate={{ 
                      scale: [1, 1.03, 1],
                      rotate: [0, 2, 0, -2, 0]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    <Search className="h-10 w-10 text-gray-400" />
                  </motion.div>
                  <h3 className="text-xl font-bold mb-2 text-gray-700">No researchers found</h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    Try adjusting your search or filter settings to find security researchers.
                  </p>
                  <motion.div 
                    className="mt-4"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setSearchQuery('');
                        setFilter('all');
                        setTimeframe('all-time');
                      }}
                      className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    >
                      <Filter className="h-4 w-4 mr-2" />
                      Reset Filters
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </motion.div>
          </TabsContent>
          
          <TabsContent value="contributions" className="mt-0">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              <Card className="mb-6 border-0 shadow-lg bg-white overflow-hidden">
                <CardHeader className="pb-3 bg-linear-to-r from-blue-50 to-white border-b border-blue-100/30">
                  <div className="flex items-center gap-3">
                    <motion.div 
                      whileHover={{ rotate: [0, -10, 20, -10, 0] }}
                      transition={{ duration: 0.6 }}
                      className="bg-blue-100 text-blue-700 p-2 rounded-full"
                    >
                      <Bug className="h-5 w-5" />
                    </motion.div>
                    <div>
                      <CardTitle className="text-xl text-blue-900">Recent Security Contributions</CardTitle>
                      <CardDescription className="text-blue-700/80">
                        Vulnerabilities reported and fixed through our Bug Bounty Program
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-6">
                  <motion.div 
                    className="space-y-6 relative"
                    variants={{
                      hidden: { opacity: 0 },
                      visible: { opacity: 1 }
                    }}
                  >
                    {/* Vertical timeline line */}
                    <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-linear-to-b from-blue-100 via-blue-50 to-transparent"></div>
                    
                    {/* Get all contributions, sort by date, and render the 10 most recent */}
                    {filteredResearchers
                      .flatMap(researcher => 
                        researcher.contributions.map((contribution, idx) => ({
                          researcher,
                          contribution,
                          key: `${researcher.id}-${idx}`
                        }))
                      )
                      .sort((a, b) => {
                        // Sort by date, newest first
                        const dateA = new Date(a.contribution.date);
                        const dateB = new Date(b.contribution.date);
                        return dateB.getTime() - dateA.getTime();
                      })
                      .slice(0, 10)
                      .map(({ researcher, contribution, key }, index) => (
                        <motion.div 
                          key={key}
                          variants={fadeInUp}
                          custom={index}
                          whileHover={{ x: 5 }}
                          transition={{ type: "spring", stiffness: 200 }}
                          className="border-b border-gray-100 last:border-0 pb-6 last:pb-0 pl-12 relative"
                        >
                          {/* Timeline dot */}
                          <motion.div 
                            className={`absolute left-3.5 top-1 w-4 h-4 rounded-full border-2 border-white z-10 shadow-sm ${
                              contribution.type === 'critical' ? 'bg-red-500' :
                              contribution.type === 'high' ? 'bg-orange-500' :
                              contribution.type === 'medium' ? 'bg-yellow-400' :
                              'bg-blue-500'
                            }`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ 
                              type: "spring", 
                              stiffness: 500, 
                              delay: index * 0.1 + 0.2 
                            }}
                          />
                          
                          <div className="flex items-start gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                <h4 className="font-medium text-gray-800">{contribution.description}</h4>
                                <motion.div
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  {getContributionBadge(contribution.type)}
                                </motion.div>
                              </div>
                              
                              <div className="flex items-center text-sm text-gray-500 mb-3">
                                <span>Discovered by </span>
                                <Link href={researcher.profileUrl || '#'} className="group">
                                  <span className="font-medium ml-1 text-blue-700 group-hover:text-blue-800 transition-colors">
                                    {researcher.name}
                                  </span>
                                </Link>
                                <span className="mx-1">•</span>
                                <time className="text-gray-500">{contribution.date}</time>
                              </div>
                              
                              {contribution.reward && (
                                <motion.div 
                                  className="inline-flex items-center bg-green-50 px-3 py-1.5 rounded-full text-sm text-green-700 border border-green-100 shadow-sm"
                                  whileHover={{ 
                                    scale: 1.05,
                                    y: -2,
                                    transition: { type: "spring", stiffness: 300 }
                                  }}
                                >
                                  <Gift className="h-3.5 w-3.5 mr-1.5" />
                                  <span className="font-medium">Reward:</span> 
                                  <motion.span 
                                    className="ml-1 font-medium"
                                    animate={{ scale: [1, 1.05, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                  >
                                    {contribution.reward}
                                  </motion.span>
                                </motion.div>
                              )}
                            </div>
                            
                            {/* Researcher avatar on right */}
                            <motion.div 
                              className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium shadow-sm border border-blue-200 mt-1"
                              whileHover={{ rotate: 10 }}
                              transition={{ type: "spring", stiffness: 200 }}
                            >
                              {researcher.avatar}
                            </motion.div>
                          </div>
                        </motion.div>
                      ))
                    }
                    
                    {filteredResearchers.flatMap(r => r.contributions).length === 0 && (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="py-16 text-center"
                      >
                        <div className="bg-gray-50 inline-block p-4 rounded-full mb-4">
                          <AlertTriangle className="h-8 w-8 text-gray-400" />
                        </div>
                        <h4 className="text-lg font-medium mb-2">No contributions found</h4>
                        <p className="text-gray-500 max-w-md mx-auto">
                          No security contributions match your current filter settings.
                        </p>
                      </motion.div>
                    )}
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
          
          <TabsContent value="about" className="mt-0">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              <motion.div variants={fadeInUp} className="md:col-span-2">
                <Card className="h-full border-0 shadow-lg overflow-hidden">
                  <CardHeader className="bg-linear-to-r from-blue-50 to-white border-b border-blue-100/30">
                    <div className="flex items-center gap-3">
                      <motion.div 
                        animate={{ rotate: [0, 10, 0, -10, 0] }}
                        transition={{ 
                          duration: 5, 
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                        className="bg-blue-100 text-blue-700 p-2 rounded-full"
                      >
                        <Trophy className="h-5 w-5" />
                      </motion.div>
                      <CardTitle className="text-xl text-blue-900">About Our Security Hall of Fame</CardTitle>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-5 p-6 relative">
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-20 -z-10 transform translate-x-1/4 -translate-y-1/4"></div>
                    
                    <motion.p
                      variants={fadeInUp}
                      className="text-gray-700 leading-relaxed"
                    >
                      The <span className="font-medium text-blue-700">RentWise Security Hall of Fame</span> celebrates security researchers who have helped 
                      improve our platform through responsible vulnerability disclosure. We believe 
                      in acknowledging the contributions of these researchers who dedicate their 
                      time and expertise to make RentWise more secure for everyone.
                    </motion.p>
                    
                    <motion.div variants={fadeInUp} className="mt-6">
                      <h3 className="font-bold text-lg text-gray-800 flex items-center mb-3">
                        <Shield className="h-5 w-5 text-blue-600 mr-2" />
                        Eligibility Criteria
                      </h3>
                      <ul className="space-y-3 pl-2">
                        {[
                          "Submit valid security vulnerabilities through our Bug Bounty Program",
                          "Follow responsible disclosure guidelines",
                          "Provide clear reproduction steps and impact assessment",
                          "Work collaboratively with our security team for verification"
                        ].map((item, i) => (
                          <motion.li 
                            key={i}
                            className="flex items-start gap-2"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 + 0.2 }}
                            whileHover={{ x: 3 }}
                          >
                            <motion.div
                              whileHover={{ scale: 1.2, rotate: 5 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                            </motion.div>
                            <span className="text-gray-700">{item}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </motion.div>
                    
                    <motion.div variants={fadeInUp} className="mt-6">
                      <h3 className="font-bold text-lg text-gray-800 flex items-center mb-3">
                        <Star className="h-5 w-5 text-yellow-400 mr-2" />
                        Recognition & Rewards
                      </h3>
                      <p className="text-gray-700 mb-3 leading-relaxed">
                        Researchers are ranked based on the number and severity of their valid submissions.
                        In addition to cash rewards for qualifying vulnerabilities, top contributors receive:
                      </p>
                      
                      <ul className="space-y-3 pl-2">
                        {[
                          "Recognition in our Security Hall of Fame",
                          "Exclusive RentWise security researcher badges",
                          "Priority processing for future submissions",
                          "Opportunities for bonus rewards for exceptional work"
                        ].map((item, i) => (
                          <motion.li 
                            key={i}
                            className="flex items-start gap-2"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 + 0.5 }}
                            whileHover={{ x: 3 }}
                          >
                            <motion.div
                              animate={{ 
                                rotate: i === 0 ? [0, 10, 0] : 0
                              }}
                              transition={{ 
                                repeat: Infinity,
                                repeatType: "reverse",
                                duration: 1.5
                              }}
                            >
                              <Award className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                            </motion.div>
                            <span className="text-gray-700">{item}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </motion.div>
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div variants={fadeInUp}>
                <Card className="h-full border-0 shadow-lg overflow-hidden">
                  <CardHeader className="bg-linear-to-r from-blue-50 to-white border-b border-blue-100/30">
                    <div className="flex items-center gap-3">
                      <motion.div 
                        whileHover={{ scale: 1.1, rotate: 10 }}
                        className="bg-blue-100 text-blue-700 p-2 rounded-full"
                      >
                        <Zap className="h-5 w-5" />
                      </motion.div>
                      <CardTitle className="text-xl text-blue-900">Join Our Bug Bounty Program</CardTitle>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-5 p-6">
                    <motion.p variants={fadeInUp} className="text-gray-700 leading-relaxed">
                      Want to see your name in our Hall of Fame? Participate in our Bug Bounty Program 
                      and help us make RentWise more secure.
                    </motion.p>
                    
                    <div className="space-y-4 mt-4">
                      <motion.div 
                        variants={fadeInUp}
                        whileHover={{ y: -5, scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        className="bg-linear-to-br from-blue-50 to-white p-5 rounded-xl shadow-sm border border-blue-100"
                      >
                        <h4 className="font-medium flex items-center gap-2 text-blue-800 mb-2">
                          <Bug className="h-5 w-5" />
                          Bug Bounty Program
                        </h4>
                        <p className="text-sm text-gray-600 mb-3">
                          Find vulnerabilities in our systems and get rewarded for responsible disclosure.
                        </p>
                        <Link href="/bug-bounty" className="group">
                          <Button variant="link" className="text-blue-600 p-0 h-auto flex items-center gap-1 group-hover:text-blue-800">
                            Learn More 
                            <motion.span
                              animate={{ x: [0, 3, 0] }}
                              transition={{ 
                                repeat: Infinity, 
                                repeatType: "reverse", 
                                duration: 1 
                              }}
                            >
                              <ChevronDown className="h-4 w-4 -rotate-90" />
                            </motion.span>
                          </Button>
                        </Link>
                      </motion.div>
                      
                      <motion.div 
                        variants={fadeInUp}
                        whileHover={{ y: -5, scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300, delay: 0.1 }}
                        className="bg-linear-to-br from-green-50 to-white p-5 rounded-xl shadow-sm border border-green-100"
                      >
                        <h4 className="font-medium flex items-center gap-2 text-green-800 mb-2">
                          <AlertTriangle className="h-5 w-5" />
                          Report Vulnerability
                        </h4>
                        <p className="text-sm text-gray-600 mb-3">
                          Found a security issue? Submit it through our secure vulnerability reporting process.
                        </p>
                        <Link href="/vulnerability" className="group">
                          <Button variant="link" className="text-green-600 p-0 h-auto flex items-center gap-1 group-hover:text-green-800">
                            Report Now 
                            <motion.span
                              animate={{ x: [0, 3, 0] }}
                              transition={{ 
                                repeat: Infinity, 
                                repeatType: "reverse", 
                                duration: 1,
                                delay: 0.2
                              }}
                            >
                              <ChevronDown className="h-4 w-4 -rotate-90" />
                            </motion.span>
                          </Button>
                        </Link>
                      </motion.div>
                      
                      <motion.div 
                        variants={fadeInUp}
                        whileHover={{ y: -5, scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300, delay: 0.2 }}
                        className="bg-linear-to-br from-yellow-100 to-white p-5 rounded-xl shadow-sm border border-yellow-200"
                      >
                        <h4 className="font-medium flex items-center gap-2 text-yellow-800 mb-2">
                          <Shield className="h-5 w-5" />
                          Security Policy
                        </h4>
                        <p className="text-sm text-gray-600 mb-3">
                          Review our security policies and responsible disclosure guidelines.
                        </p>
                        <Link href="/security-policy" className="group">
                          <Button variant="link" className="text-yellow-600 p-0 h-auto flex items-center gap-1 group-hover:text-yellow-800">
                            View Policy
                            <motion.span
                              animate={{ x: [0, 3, 0] }}
                              transition={{ 
                                repeat: Infinity, 
                                repeatType: "reverse", 
                                duration: 1,
                                delay: 0.4
                              }}
                            >
                              <ChevronDown className="h-4 w-4 -rotate-90" />
                            </motion.span>
                          </Button>
                        </Link>
                      </motion.div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Mock data
const mockResearchers: Researcher[] = [
  {
    id: 1,
    name: "Jane Smith",
    username: "janesmith",
    avatar: "JS",
    achievements: { critical: 3, high: 5, medium: 2, low: 0 },
    badges: ["🏆 Top Contributor", "Web Security Expert", "API Specialist"],
    specialties: ["web", "api", "authentication"],
    totalRewards: "$12,500",
    joinedDate: "2021-06-15",
    rank: 1,
    featured: true,
    country: "United States",
    contributions: [
      {
        date: "2023-03-15",
        type: "critical",
        description: "Authentication bypass in login flow",
        reward: "$4,500"
      },
      {
        date: "2022-12-03",
        type: "high",
        description: "XSS vulnerability in property details page",
        reward: "$1,800"
      },
      {
        date: "2022-08-27",
        type: "critical",
        description: "SQL injection in search functionality",
        reward: "$3,200"
      }
    ]
  },
  {
    id: 2,
    name: "John Doe",
    username: "johndoe",
    avatar: "JD",
    achievements: { critical: 1, high: 3, medium: 7, low: 2 },
    badges: ["🔍 Security Expert", "Mobile App Specialist"],
    specialties: ["mobile", "api"],
    totalRewards: "$7,200",
    joinedDate: "2022-01-10",
    rank: 2,
    featured: true,
    country: "Canada",
    contributions: [
      {
        date: "2023-04-22",
        type: "high",
        description: "Authorization flaw in payment processing",
        reward: "$1,500"
      },
      {
        date: "2023-01-08",
        type: "critical",
        description: "Remote code execution vulnerability",
        reward: "$4,000"
      }
    ]
  },
  {
    id: 3,
    name: "Alex Johnson",
    username: "alexj",
    avatar: "AJ",
    achievements: { critical: 0, high: 4, medium: 3, low: 5 },
    badges: ["⚡ Rising Star"],
    specialties: ["web", "configuration"],
    totalRewards: "$5,900",
    joinedDate: "2022-07-15",
    rank: 3,
    featured: true,
    country: "United Kingdom",
    contributions: [
      {
        date: "2023-05-03",
        type: "high",
        description: "CSRF vulnerability in account settings",
        reward: "$1,700"
      },
      {
        date: "2023-02-14",
        type: "high",
        description: "OAuth implementation security flaw",
        reward: "$1,800"
      }
    ]
  },
  {
    id: 4,
    name: "Sarah Williams",
    username: "sarahw",
    avatar: "SW",
    achievements: { critical: 0, high: 2, medium: 4, low: 3 },
    badges: ["API Security Specialist"],
    specialties: ["api", "web"],
    totalRewards: "$3,700",
    joinedDate: "2022-09-20",
    rank: 4,
    featured: false,
    country: "Australia",
    contributions: [
      {
        date: "2023-04-12",
        type: "medium",
        description: "Information disclosure in API responses",
        reward: "$450"
      },
      {
        date: "2023-01-30",
        type: "high",
        description: "Missing authentication in API endpoint",
        reward: "$1,600"
      }
    ]
  },
  {
    id: 5,
    name: "Michael Chen",
    username: "mchen",
    avatar: "MC",
    achievements: { critical: 0, high: 1, medium: 5, low: 4 },
    badges: ["Mobile Security Expert"],
    specialties: ["mobile", "android"],
    totalRewards: "$2,800",
    joinedDate: "2022-11-05",
    rank: 5,
    featured: false,
    country: "Singapore",
    contributions: [
      {
        date: "2023-05-01",
        type: "medium",
        description: "Insecure data storage in mobile app",
        reward: "$500"
      },
      {
        date: "2023-02-18",
        type: "high",
        description: "Authentication flaw in mobile API",
        reward: "$1,200"
      }
    ]
  },
  {
    id: 6,
    name: "Emma Garcia",
    username: "emmag",
    avatar: "EG",
    achievements: { critical: 0, high: 1, medium: 3, low: 5 },
    badges: ["Web Security Specialist"],
    specialties: ["web", "xss"],
    totalRewards: "$2,200",
    joinedDate: "2022-10-12",
    rank: 6,
    featured: false,
    country: "Spain",
    contributions: [
      {
        date: "2023-03-28",
        type: "medium",
        description: "XSS in review submission form",
        reward: "$400"
      },
      {
        date: "2022-12-15",
        type: "high",
        description: "DOM-based XSS in property search",
        reward: "$1,100"
      }
    ]
  },
  {
    id: 7,
    name: "David Kim",
    username: "dkim",
    avatar: "DK",
    achievements: { critical: 0, high: 0, medium: 2, low: 7 },
    badges: ["Dedicated Researcher"],
    specialties: ["web", "configuration"],
    totalRewards: "$1,450",
    joinedDate: "2023-01-25",
    rank: 7,
    featured: false,
    country: "South Korea",
    contributions: [
      {
        date: "2023-04-30",
        type: "medium",
        description: "Security misconfiguration in error handling",
        reward: "$350"
      },
      {
        date: "2023-03-12",
        type: "medium",
        description: "Sensitive information disclosure in logs",
        reward: "$400"
      }
    ]
  },
  {
    id: 8,
    name: "Olivia Thompson",
    username: "oliviat",
    avatar: "OT",
    achievements: { critical: 0, high: 0, medium: 1, low: 3 },
    badges: ["New Contributor"],
    specialties: ["web"],
    totalRewards: "$500",
    joinedDate: "2023-03-15",
    rank: 8,
    featured: false,
    country: "New Zealand",
    contributions: [
      {
        date: "2023-05-10",
        type: "low",
        description: "CORS misconfiguration",
        reward: "$150"
      },
      {
        date: "2023-04-05",
        type: "medium",
        description: "Insecure direct object reference",
        reward: "$350"
      }
    ]
  }
]; 
